import { Camera, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { HelpTip } from '../HelpTip';
import { Button } from '../Button';
import { useDeferredError } from './useDeferredError';

/** What the field reports upward: a verified-looking image, ready for the wire. */
export interface PhotoValue {
  /** `image/png` or `image/jpeg`. The server re-checks this against the payload's magic bytes. */
  contentType: string;
  /** Base64 without the data-URL prefix — `byte[]` on the wire. */
  bytes: string;
}

const ACCEPTED = ['image/png', 'image/jpeg'];

/**
 * The shared photograph input: a round preview, a button to choose one, a button to clear it.
 *
 * **Never a bare `<input type="file">`.** The native control cannot be styled, states its own
 * filename in a font nothing else on the form uses, and gives no preview — so it is hidden
 * behind a label and this component owns the affordance, exactly as the other shared inputs
 * own theirs.
 *
 * It keeps every promise they make: one label line so a row of fields still aligns, the
 * description in a `<HelpTip>` with an `sr-only` twin wired to `aria-describedby`, the error
 * below the control in the flow, and a disabled state that reads as disabled rather than
 * dead.
 */
export function PhotoField({
  id,
  label,
  description,
  value,
  existingUrl,
  onChange,
  removable = true,
  error,
  disabled = false,
  maxBytes = 2 * 1024 * 1024,
}: {
  id: string;
  label: string;
  description?: string;
  /** The chosen image, or null when nothing has been chosen this session. */
  value: PhotoValue | null;
  /**
   * A photograph the record already has, shown until one is chosen.
   *
   * Separate from `value` because they are different facts: one is stored, the other is
   * pending. Clearing sets `value` to null and hides this too, so the preview never claims a
   * photograph the reader has just removed.
   */
  existingUrl?: string | null;
  onChange: (next: PhotoValue | null) => void;
  /**
   * Whether the STORED photograph may be removed — `personnel.delete`, where the write is
   * `personnel.update`. A pending choice can always be undone, whatever this says: undoing
   * a pick is not a delete, so with this false the button reverts to the stored image
   * instead of clearing it.
   */
  removable?: boolean;
  error?: string;
  disabled?: boolean;
  maxBytes?: number;
}) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [cleared, setCleared] = useState(false);

  // The object URL is revoked when it is replaced or the field unmounts: a blob URL held
  // after its image is gone is a leak the browser cannot collect on its own.
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  /*
    The parent owns the value, so a form that resets its state must reset the preview with
    it — otherwise the disc keeps showing an image the request no longer carries.
  */
  useEffect(() => {
    if (value === null) setPreview(null);
  }, [value]);

  /*
    A required-but-empty field must not open red. `useDeferredError` holds back an error the
    control has carried since it mounted until the reader has touched it, and shows anything
    that arrives later — a server rejection included — immediately.

    Only the `error` prop is deferred. `localError` is raised by choosing a file — it cannot
    exist at mount, so it is a reaction by construction and is shown the moment it appears.
  */
  const { error: shownError, markTouched } = useDeferredError(error);

  const shown = preview ?? (cleared ? null : (existingUrl ?? null));
  const describedBy = [
    description ? `${fieldId}-desc` : null,
    shownError || localError ? `${fieldId}-err` : null,
  ]
    .filter(Boolean)
    .join(' ');

  function choose(file: File | undefined) {
    setLocalError(null);

    if (!file) return;

    if (!ACCEPTED.includes(file.type)) {
      setLocalError('Choose a PNG or a JPEG.');
      return;
    }

    if (file.size > maxBytes) {
      setLocalError(`Choose an image ${Math.floor(maxBytes / (1024 * 1024))} MB or smaller.`);
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => setLocalError('That file could not be read.');
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // A data URL is `data:<type>;base64,<payload>` — the wire wants the payload alone.
      const comma = result.indexOf(',');

      if (comma < 0) {
        setLocalError('That file could not be read.');
        return;
      }

      if (preview) URL.revokeObjectURL(preview);

      setPreview(URL.createObjectURL(file));
      setCleared(false);
      onChange({ contentType: file.type, bytes: result.slice(comma + 1) });
    };

    reader.readAsDataURL(file);
  }

  function clear() {
    if (preview) URL.revokeObjectURL(preview);

    setPreview(null);
    setLocalError(null);
    // Only a reader who may delete the stored photograph clears it; for everyone else this
    // undoes the pick and the stored image comes back.
    setCleared(removable);
    onChange(null);

    // Without this the same file cannot be chosen again: the input still holds it, so
    // picking it produces no change event.
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={fieldId} className="text-sm text-slate-700">
          {label}
        </label>

        {description ? <HelpTip label={description} /> : null}
      </div>

      {description ? (
        <span id={`${fieldId}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      <div className="flex items-center gap-4">
        <div
          className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-300 bg-slate-50"
          aria-hidden="true"
        >
          {shown ? (
            <img src={shown} alt="" className="h-full w-full object-cover" />
          ) : (
            <Camera className="h-6 w-6 text-slate-400" />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            id={fieldId}
            name={fieldId}
            type="file"
            accept={ACCEPTED.join(',')}
            disabled={disabled}
            aria-describedby={describedBy || undefined}
            aria-invalid={shownError || localError ? true : undefined}
            className="sr-only"
            onChange={(e) => {
              markTouched();
              choose(e.target.files?.[0]);
            }}
          />

          <div className="flex items-center gap-2">
            {/* The label IS the button: it opens the hidden input with no script at all. */}
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              leading={<Camera className="h-4 w-4" />}
            >
              {shown ? 'Replace' : 'Choose photo'}
            </Button>

            {shown && (removable || preview !== null) ? (
              <Button
                variant="tertiary"
                disabled={disabled}
                onClick={clear}
                leading={removable ? <Trash2 className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
              >
                {removable ? 'Remove' : 'Undo'}
              </Button>
            ) : null}
          </div>

          <p className="text-xs text-slate-500">
            PNG or JPEG, up to {Math.floor(maxBytes / (1024 * 1024))} MB.
          </p>
        </div>
      </div>

      {shownError || localError ? (
        <p id={`${fieldId}-err`} className="text-xs text-red-600" role="alert">
          {shownError ?? localError}
        </p>
      ) : null}
    </div>
  );
}
